import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, ButtonSize, LinkSize, Text, TextColor, TextSize } from '@vizit/atoms';
import { Form, FormTextField } from '@vizit/forms';
import { VizitLogo } from '@vizit/icons';
import { Heading, HeadingVariant, RouterLink } from '@vizit/molecules';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { SsoErrorAlert, type SsoLoginError } from '../sso-error-alert';

/** Form schema for the email step. */
type SsoEmailStepSchema = {
  /** @type {string} The user's email address. */
  email: string;
};

/** Form schema for the password step. */
type SsoPasswordStepSchema = {
  /** @type {string} The user's email address. */
  email: string;
  /** @type {string} The user's password. */
  password: string;
};

enum SsoLoginStep {
  Email = 'email',
  Password = 'password',
}

enum SsoCheckResult {
  Sso = 'sso',
  Password = 'password',
  Error = 'error',
}

interface SsoLoginFormProps {
  /**
   * The current error to display in the form.
   *
   * @optional
   * @type {SsoLoginError | null}
   * @default null
   */
  error?: SsoLoginError | null;
  /**
   * Callback to check if the user's email uses SSO authentication.
   *
   * @required
   * @type {(email: string) => Promise<SsoCheckResult>}
   */
  onSsoCheck: (email: string) => Promise<SsoCheckResult>;
  /**
   * Callback to submit email and password credentials.
   *
   * @required
   * @type {(data: { email: string; password: string }) => Promise<void>}
   */
  onSubmit: (data: { email: string; password: string }) => Promise<void>;
  /**
   * Callback to clear the current error state.
   *
   * @optional
   * @type {() => void}
   */
  onErrorClear?: () => void;
}

function SsoLoginForm(props: SsoLoginFormProps) {
  const { error, onSsoCheck, onSubmit, onErrorClear } = props;

  const [step, setStep] = React.useState<SsoLoginStep>(SsoLoginStep.Email);
  const [isCheckingSso, setIsCheckingSso] = React.useState(false);

  const emailSchema = yup.object().shape({
    email: yup.string().email('Invalid email format. Please try again.').required('Please enter your email.'),
  });

  const passwordSchema = yup.object().shape({
    email: yup.string().email('Invalid email format. Please try again.').required('Please enter your email.'),
    password: yup.string().required('Please enter your password.'),
  });

  const emailForm = useForm<SsoEmailStepSchema>({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: '' },
    mode: 'all',
    delayError: 600,
  });

  const passwordForm = useForm<SsoPasswordStepSchema>({
    resolver: yupResolver(passwordSchema),
    defaultValues: { email: '', password: '' },
    mode: 'all',
    delayError: 600,
  });

  React.useEffect(() => {
    const emailSub = emailForm.watch(() => {
      if (error) onErrorClear?.();
    });
    const passwordSub = passwordForm.watch(() => {
      if (error) onErrorClear?.();
    });
    return () => {
      emailSub.unsubscribe();
      passwordSub.unsubscribe();
    };
  }, [error, onErrorClear, emailForm, passwordForm]);

  const handleEmailSubmit = async ({ email }: SsoEmailStepSchema) => {
    setIsCheckingSso(true);
    try {
      const result = await onSsoCheck(email);
      if (result === SsoCheckResult.Password) {
        passwordForm.setValue('email', email);
        setStep(SsoLoginStep.Password);
        setIsCheckingSso(false);
      } else if (result === SsoCheckResult.Error) {
        setIsCheckingSso(false);
      }
    } catch {
      setIsCheckingSso(false);
    }
  };

  const handlePasswordSubmit = async ({ email, password }: SsoPasswordStepSchema) => {
    await onSubmit({ email, password });
  };

  const handleBack = () => {
    setStep(SsoLoginStep.Email);
    emailForm.setValue('email', passwordForm.getValues('email'));
    onErrorClear?.();
  };

  return (
    <div className="flex-grow flex flex-col justify-center items-center gap-16" data-testid="sso-login-form">
      <div className="flex flex-col items-center gap-16">
        <VizitLogo aria-label="Vizit Logo" />
        <div className="flex flex-col items-center justify-center gap-4 min-h-[70px]">
          <Heading variant={HeadingVariant.H5}>Log in to your account</Heading>
          <Text size={TextSize.Md} color={TextColor.Tertiary}>
            {step === SsoLoginStep.Email
              ? 'Enter your email to continue.'
              : 'Welcome back! Please enter your password.'}
          </Text>
        </div>
      </div>
      <div className="flex flex-col justify-center items-center gap-8">
        {step === SsoLoginStep.Email ? (
          <Form className="min-w-[380px] max-w-[380px] gap-4" onSubmit={emailForm.handleSubmit(handleEmailSubmit)}>
            {error && <SsoErrorAlert error={error} />}
            <FormTextField
              type="email"
              label="Email"
              control={emailForm.control}
              name="email"
              inputProps={{ 'data-testid': 'sso-email-input' }}
            />
            <Button aria-label="Continue" type="submit" isLoading={isCheckingSso} data-testid="sso-continue-button">
              Continue
            </Button>
          </Form>
        ) : (
          <Form
            className="min-w-[380px] max-w-[380px] gap-4"
            onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}
          >
            {error && <SsoErrorAlert error={error} />}
            <div className="flex items-center gap-2">
              <Text size={TextSize.Sm} color={TextColor.Tertiary}>
                {passwordForm.getValues('email')}
              </Text>
              <Button
                level={ButtonLevel['Text-Primary']}
                size={ButtonSize.Sm}
                onPress={handleBack}
                data-testid="sso-change-email-button"
              >
                Not you?
              </Button>
            </div>
            <FormTextField
              inputProps={{ className: 'pendo-ignore', 'data-testid': 'sso-password-input' }}
              type="password"
              label="Password"
              control={passwordForm.control}
              name="password"
            />
            <Button
              aria-label="Sign in"
              type="submit"
              isLoading={passwordForm.formState.isSubmitting}
              data-testid="sso-submit-button"
            >
              Sign in
            </Button>
          </Form>
        )}
        {step === SsoLoginStep.Password && (
          <RouterLink size={LinkSize.Sm} to="/forgot-password" data-testid="sso-forgot-password-link">
            Forgot your password?
          </RouterLink>
        )}
      </div>
    </div>
  );
}

export { SsoLoginForm, SsoCheckResult };
export type { SsoLoginFormProps };
